import axios from 'axios';

export const WAREHOUSE_ERROR_CODES = [
  'PRODUCT_ARCHIVED',
  'PRODUCT_KIND_MISMATCH',
  'RX_NOT_SELLABLE',
  'BATCH_EXPIRED',
  'BATCH_REQUIRED',
  'BATCH_PRODUCT_MISMATCH',
  'DOCUMENT_NOT_DRAFT',
  'DOCUMENT_ALREADY_CANCELLED',
  'DOCUMENT_TYPE_NOT_CANCELLABLE',
  'NIP_INVALID',
  'INVALID_QUANTITY',
  'EMPTY_DOCUMENT',
  // Revision R1
  'WAREHOUSE_DISABLED',
  'UNIT_LOCKED',
  'OPERATION_KEY_REUSED',
  'MOVEMENT_NOT_ASSIGNABLE',
  'DUPLICATE_LINE_ID',
  'MA_HOLDER_REQUIRED',
  'PRESCRIPTION_FACTS_REQUIRED',
  'RECIPIENT_REQUIRED',
  'SUPPLIER_ADDRESS_REQUIRED',
  // Phase 2: a Medication a treatment took from stock is deleted in the treatment.
  'MEDICATION_HAS_TREATMENT_STOCK',
  // Phase 4: a Demand Request status change its current status does not allow.
  'DEMAND_REQUEST_TRANSITION_INVALID',
  // A free-text Demand Request linked to a catalogue Product it cannot take.
  'DEMAND_REQUEST_LINK_INVALID',
  // A Demand Request unlinked from its Product that was never free-text or is closed.
  'DEMAND_REQUEST_UNLINK_INVALID',
  // #688 R1-2: a handed-over Rx drug records its recipient's address.
  'RECIPIENT_ADDRESS_REQUIRED',
  // #716: a visit that did not take place takes no Sale.
  'VISIT_NOT_SELLABLE',
] as const;

export type WarehouseErrorCode = (typeof WAREHOUSE_ERROR_CODES)[number];

interface ErrorBody {
  code?: unknown;
  message?: unknown;
}

const isWarehouseErrorCode = (code: unknown): code is WarehouseErrorCode =>
  typeof code === 'string' &&
  (WAREHOUSE_ERROR_CODES as readonly string[]).includes(code);

/** The backend's domain code of a refused warehouse request, if it named one. */
export function warehouseErrorCode(
  error: unknown,
): WarehouseErrorCode | undefined {
  if (!axios.isAxiosError(error) || !error.response) {
    return undefined;
  }
  const { code } = (error.response.data ?? {}) as ErrorBody;
  return isWarehouseErrorCode(code) ? code : undefined;
}

/** Polish text for a failed warehouse request; never shows English validator output. */
export function warehouseErrorMessage(
  error: unknown,
  translate: (key: string) => string,
): string {
  if (axios.isAxiosError(error) && error.response) {
    const body = (error.response.data ?? {}) as ErrorBody;
    const { status } = error.response;
    if (isWarehouseErrorCode(body.code)) {
      return translate(`warehouse.errors.${body.code}`);
    }
    // F-4: the attachment upload's 413 (payload too large) is checked before
    // the generic fallback, and before the 400/404/409 branches, so a
    // proxy/nginx 413 with no JSON body still gets the Polish message.
    if (status === 413) {
      return translate('warehouse.documents.attachment.tooLarge');
    }
    if (status === 400 && typeof body.message === 'string') {
      return body.message;
    }
    if (status === 404) {
      return translate('warehouse.common.notFound');
    }
    if (status === 409) {
      return translate('warehouse.suppliers.nipTaken');
    }
  }
  return translate('warehouse.common.genericError');
}

/** The backend answered 404: the record does not exist in this organization. */
export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * F-7: cancel has no operation key, so a retry after a lost answer is refused
 * as already cancelled; the document is cancelled either way.
 */
export function isAlreadyCancelled(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    warehouseErrorCode(error) === 'DOCUMENT_ALREADY_CANCELLED'
  );
}
